import { Box, Button, Typography, useTheme } from '@mui/material';
import Dialog from 'components/dialog';
import Divider from 'components/divider';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import { ClipboardList, Cylinder } from 'lucide-react';
import React from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { OrderListItemDistributionDetails } from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/types/order-list-item-distribution-details.type';

type Props = {
  distributionDetails: OrderListItemDistributionDetails;
};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders.distributionDetails';

const DistributionDetailsDialog: React.FC<Props> = ({ distributionDetails }) => {
  const intl = useIntl();
  const theme = useTheme();
  const dialogTitle = intl.formatMessage({ id: `${PAGE_NAME_ID}.title` }, { orderNumber: distributionDetails.orderNumber });

  return (
    <Dialog
      maxWidth="md"
      title={dialogTitle}
      button={
        <Button
          type="button"
          size="small"
          variant="outlined"
          startIcon={<ClipboardList size={24} />}
          sx={{
            borderColor: theme.palette.bluViu.main,
            color: theme.palette.bluViu.main,
          }}>
          <FormattedMessage id={`${PAGE_NAME_ID}.showDistribution`} />
        </Button>
      }>
      <Box display="flex" flexDirection="column" gap={3}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" fontWeight="bold">
              <FormattedMessage id={`${PAGE_NAME_ID}.updatedAt`} />
            </Typography>
            <Typography variant="body2">
              <FormattedDate
                value={distributionDetails.updatedAt}
                day="2-digit"
                month="short"
                year="numeric"
                hour="2-digit"
                minute="2-digit"
                hour12={false}
              />
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" fontWeight="bold">
              <FormattedMessage id={`${PAGE_NAME_ID}.updatedBy`} />
            </Typography>
            <Typography variant="body2">{distributionDetails.updatedBy || <FormattedMessage id="general.notApplicable" />}</Typography>
          </Box>
        </Box>
        <Divider dark />
        <List
          dark
          options={[
            {
              id: 'siloName',
              label: intl.formatMessage({ id: `${PAGE_NAME_ID}.silo` }),
            },
            {
              id: 'itemName',
              label: intl.formatMessage({ id: `${PAGE_NAME_ID}.product` }),
            },
            {
              id: 'quantity',
              label: intl.formatMessage({ id: `${PAGE_NAME_ID}.quantity` }),
              align: 'right',
            },
          ]}>
          {distributionDetails.siloDistributions.map((distribution) => (
            <ListRow key={`${distribution.siloId}-${distribution.itemNumber}`}>
              <ListCell>
                <Box display="flex" flexDirection="column">
                  <Typography>{distribution.siloName || <FormattedMessage id="general.notApplicable" />}</Typography>
                  {distribution.isExtraStorage && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Cylinder size={14} color={theme.palette.primary.main} />
                      <small>
                        <FormattedMessage id={`${PAGE_NAME_ID}.extraStorage`} />
                      </small>
                    </Box>
                  )}
                </Box>
              </ListCell>
              <ListCell>
                <Box display="flex" flexDirection="column">
                  <Typography>{distribution.itemName || <FormattedMessage id="general.notApplicable" />}</Typography>
                  <Typography color={theme.palette.darkBlue.o75}>
                    {distribution.itemNumber || <FormattedMessage id="general.notApplicable" />}
                  </Typography>
                </Box>
              </ListCell>
              <ListCell align="right">
                <FormattedNumber maximumFractionDigits={2} value={distribution.quantity || 0} style="unit" unit="kilogram" />
              </ListCell>
            </ListRow>
          ))}
        </List>
      </Box>
    </Dialog>
  );
};

export default React.memo(DistributionDetailsDialog);
